/**
 * Typed API client. Every module uses this — no raw fetch() in module code.
 *
 * Cloudflare Access handles auth via cookie, so requests just need credentials.
 * A 403 from an adult-only route is expected for children and must be handled,
 * not treated as a crash.
 */
import type { ApiError } from '../shared/types';

export class ApiRequestError extends Error {
  constructor(
    public status: number,
    public body: ApiError
  ) {
    super(body.detail ?? body.error);
  }
  get isForbidden() {
    return this.status === 403;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let payload: ApiError = { error: `http_${res.status}` };
    try {
      payload = await res.json();
    } catch {
      /* non-JSON error body */
    }
    throw new ApiRequestError(res.status, payload);
  }

  if (res.status === 204) return undefined as T;

  // A 200 that isn't JSON means we didn't reach the API at all — we hit the SPA's
  // own index.html fallback, because the Worker route isn't serving this hostname.
  // Say that, instead of letting a JSON parse error surface as a mystery.
  const type = res.headers.get('content-type') ?? '';
  if (!type.includes('application/json')) {
    throw new ApiRequestError(res.status, {
      error: 'api_unreachable',
      detail: 'The API did not respond on this hostname.',
    });
  }

  return res.json() as Promise<T>;
}

/**
 * Multipart upload (meal photos). Separate from request() because the body must
 * NOT be JSON-stringified and the browser must set its own multipart boundary.
 * Same error type, so callers handle failures identically.
 */
async function upload<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: 'POST',
    credentials: 'include',
    body: form, // no Content-Type header — the browser sets the boundary
  });

  if (!res.ok) {
    let payload: ApiError = { error: `http_${res.status}` };
    try {
      payload = await res.json();
    } catch {
      /* non-JSON error body */
    }
    throw new ApiRequestError(res.status, payload);
  }

  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
  upload,
};

/** cents -> "$1,234.56" */
export function money(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

/** "$1,234.56" | "1234.56" -> 123456 cents. Returns null if unparseable. */
export function parseMoney(input: string): number | null {
  const cleaned = input.replace(/[$,\s]/g, '');
  if (!/^-?\d*\.?\d{0,2}$/.test(cleaned) || cleaned === '' || cleaned === '-') return null;
  return Math.round(parseFloat(cleaned) * 100);
}

/** Monday of the week containing `date`, as YYYY-MM-DD. */
export function weekStart(date = new Date()): string {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // Mon = 0
  d.setDate(d.getDate() - day);
  return d.toISOString().slice(0, 10);
}

/** Current period as YYYY-MM. */
export function currentPeriod(date = new Date()): string {
  return date.toISOString().slice(0, 7);
}
